import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, GitCompare, Star, Truck, ShieldCheck, ChevronRight, Check, Building2, Sun, Ruler, Volume2, Cpu } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { AspectRatio } from "../components/ui/aspect-ratio";
import { ProductCard, BadgePill } from "../components/ProductCard";
import { RoomVisual } from "../components/RoomVisual";
import { getIcon } from "../lib/icons";
import { getProduct, formatEUR } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { addToCart, toggleCompare, compare } = useStore();
  const [data, setData] = useState(null);
  const [img, setImg] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); setData(null); getProduct(slug, lang).then((d) => { setData(d); setImg(0); }).catch(() => {}); }, [slug, lang]);

  if (!data) return <div className="pt-32 pb-20 container-lumeo text-center text-muted-foreground">{t("common.loading")}</div>;
  const p = data.product; const s = p.specs;
  const gallery = p.gallery?.length ? p.gallery : [p.image];
  const imgWidth = +(3 / s.throw_ratio).toFixed(2);
  const diag = Math.round((imgWidth / 0.87157) / 0.0254);
  const add = () => { addToCart({ slug: p.slug, name: p.name, price_eur: p.price_eur, image: p.image, kind: "product" }); toast.success(`${p.name} \u2014 ${t("common.added")}`); };
  const specRows = [
    [t("specL.resolution"), s.resolution], [t("specL.supported"), s.supported_res], [t("specL.brightness"), `${s.lumens_ansi} ANSI`],
    [t("specL.throwType"), t(`throwTypes.${s.throw_type}`)], [t("specL.throwRatio"), `${s.throw_ratio}:1`], [t("specL.contrast"), s.contrast],
    [t("specL.smartOs"), s.smart_os], [t("specL.speakers"), s.speakers], [t("specL.noise"), `${s.noise_db} dB`],
    [t("specL.keystone"), s.keystone], [t("specL.weight"), `${s.weight_kg} kg`], [t("specL.lampLife"), `${s.lamp_life_hours?.toLocaleString()} h`],
  ];

  return (
    <div className="pt-24 pb-16">
      <div className="container-lumeo">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">{t("common.home")}</Link><ChevronRight className="h-3 w-3" />
          <Link to="/shop" className="hover:text-foreground">{t("nav.shop")}</Link><ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{p.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* staged gallery */}
          <div>
            <div className="relative rounded-2xl card-ceramic overflow-hidden">
              <div className="absolute inset-0 scanlines opacity-40" />
              <div className="absolute inset-0" style={{ background: "radial-gradient(600px 300px at 50% 0%, rgba(86,227,255,0.14), transparent 70%)" }} />
              <AspectRatio ratio={4 / 3}><img src={gallery[img]} alt={p.name} className="h-full w-full object-contain p-8" /></AspectRatio>
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-2">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setImg(i)} className={`h-16 w-16 rounded-lg overflow-hidden bg-black/40 hairline ${img === i ? "shadow-glow-cyan" : "opacity-70"}`}>
                    <img src={g} alt="" className="h-full w-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* info */}
          <div>
            <div className="flex flex-wrap gap-1.5 mb-3">{(p.badges || []).map((b) => <BadgePill key={b} label={b} />)}</div>
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{p.brand}</span>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">{p.name}</h1>
            <div className="flex items-center gap-2 mt-2"><span className="inline-flex items-center gap-1 text-amberx text-sm"><Star className="h-4 w-4 fill-amberx" />{p.rating}</span><span className="text-xs text-muted-foreground">({p.reviews_count} {t("product.reviews")})</span></div>
            <p className="mt-4 text-foreground/85">{p.highlight}</p>

            <div className="mt-5 flex items-end gap-3">
              <span className="font-display text-3xl">{formatEUR(p.price_eur)}</span>
              {p.compare_at_eur && <span className="text-muted-foreground line-through">{formatEUR(p.compare_at_eur)}</span>}
              {p.compare_at_eur && <span className="chip text-mintx mb-1">{t("common.save")} {formatEUR(p.compare_at_eur - p.price_eur)}</span>}
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SpecMini icon={Sun} label={`${s.lumens_ansi} ANSI`} />
              <SpecMini icon={Cpu} label={s.resolution} />
              <SpecMini icon={Ruler} label={`${s.throw_ratio}:1`} />
              <SpecMini icon={Volume2} label={`${s.noise_db} dB`} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={add} size="lg" data-testid="pdp-add-to-cart" className="btn-glow border-0 h-12 flex-1 min-w-[160px]"><Plus className="h-5 w-5 mr-1" />{t("product.buyNow")}</Button>
              <Button onClick={() => { toggleCompare(p.slug); toast(t("nav.compare")); }} variant="secondary" size="lg" className="h-12 hairline"><GitCompare className="h-5 w-5 mr-1" />{compare.includes(p.slug) ? t("common.remove") : t("common.compareAdd")}</Button>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl hairline p-3 flex items-center gap-3 text-sm"><Truck className="h-5 w-5 text-cyanx" />{t("product.shipping")}</div>
              <div className="rounded-xl hairline p-3 flex items-center gap-3 text-sm"><ShieldCheck className="h-5 w-5 text-cyanx" />{t("product.warranty")}</div>
            </div>
          </div>
        </div>

        {/* Projection preview */}
        <section className="mt-16 grid lg:grid-cols-2 gap-8 items-center">
          <div className="rounded-2xl card-ceramic p-4"><RoomVisual distanceM={3} refDiagInch={diag} screenInch={Math.round(diag / 10) * 10} /></div>
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanx/80">{t("product.projectionPreview")}</span>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl">{t("product.inThisRoom")} <span className="text-glow-cyan">{diag}"</span></h2>
            <p className="mt-3 text-muted-foreground">{t("simSection.body")}</p>
            <Button asChild variant="secondary" className="mt-4 hairline"><Link to="/simulator"><Ruler className="h-4 w-4 mr-2 text-cyanx" />{t("simSection.cta")}</Link></Button>
          </div>
        </section>

        {/* Description + specs */}
        <div className="mt-16 grid lg:grid-cols-[1fr_360px] gap-8">
          <div>
            <h2 className="font-display text-2xl mb-3">{t("product.description")}</h2>
            <p className="text-foreground/80 leading-relaxed">{p.description}</p>
            <h3 className="font-display text-xl mt-8 mb-3">{t("product.idealFor")}</h3>
            <div className="flex flex-wrap gap-2">{(p.use_cases || []).map((u) => <span key={u} className="chip">{t(`useCases.${u}`)}</span>)}</div>
            <Accordion type="single" collapsible defaultValue="tech" className="mt-8">
              <AccordionItem value="tech">
                <AccordionTrigger className="font-display text-lg">{t("product.technical")}</AccordionTrigger>
                <AccordionContent>
                  <div className="grid sm:grid-cols-2 gap-x-6">
                    {specRows.map(([k, v]) => (
                      <div key={k} className="flex justify-between py-2 border-b border-border text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq">
                <AccordionTrigger className="font-display text-lg">{t("product.faqTitle")}</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm">
                    <div><p className="font-medium">{t("pdpFaq.q4k")}</p><p className="text-muted-foreground">{t("pdpFaq.a4k").replace("{res}", s.resolution).replace("{sup}", s.supported_res)}</p></div>
                    <div><p className="font-medium">{t("pdpFaq.qNoise")}</p><p className="text-muted-foreground">{t("pdpFaq.aNoise").replace("{db}", s.noise_db)}</p></div>
                    <div><p className="font-medium">{t("pdpFaq.qCeiling")}</p><p className="text-muted-foreground">{t("pdpFaq.aCeiling")}</p></div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="mt-6 rounded-xl card-ceramic p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3"><Building2 className="h-6 w-6 text-amberx" /><span className="text-sm">{t("product.bulkInquiry")}</span></div>
              <Button asChild variant="secondary" className="hairline"><Link to="/wholesale">{t("wholesale.submit")}</Link></Button>
            </div>
          </div>

          {/* accessories cross-sell */}
          <aside className="space-y-3">
            <h3 className="font-display text-lg">{t("product.completeSetup")}</h3>
            {data.accessories?.slice(0, 5).map((a) => {
              const Icon = getIcon(a.icon);
              return (
                <div key={a.slug} className="flex items-center gap-3 rounded-xl card-ceramic p-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-cyanx/10"><Icon className="h-5 w-5 text-cyanx" /></span>
                  <div className="flex-1 min-w-0"><p className="text-sm truncate">{lang === "hr" ? a.name_hr : a.name_en}</p><p className="font-mono text-xs text-muted-foreground">{formatEUR(a.price_eur)}</p></div>
                  <button onClick={() => { addToCart({ slug: a.slug, name: lang === "hr" ? a.name_hr : a.name_en, price_eur: a.price_eur, kind: "accessory" }); toast.success(t("common.added")); }} className="h-8 w-8 rounded-lg hairline inline-flex items-center justify-center hover:shadow-glow-cyan"><Plus className="h-4 w-4" /></button>
                </div>
              );
            })}
          </aside>
        </div>

        {/* related */}
        {data.related?.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl mb-6">{t("product.relatedProjectors")}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{data.related.map((r, i) => <ProductCard key={r.slug} product={r} index={i} />)}</div>
          </section>
        )}
      </div>
    </div>
  );
}

const SpecMini = ({ icon: Icon, label }) => (
  <div className="rounded-lg hairline p-2.5 flex flex-col items-center gap-1 text-center"><Icon className="h-4 w-4 text-cyanx" /><span className="font-mono text-xs">{label}</span></div>
);
